import React, { useState } from "react";
import { Instagram, Mail, Send, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import Watermark from "../components/Watermark";
import { sendContact } from "../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill out all fields.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await sendContact(form);
      if (result?.ok) {
        toast.success("Message received. We respond within 24 hours.");
        setForm({ name: "", email: "", message: "" });
      } else {
        toast.error("Couldn't send message. Try again in a moment.");
      }
    } catch (err) {
      toast.error("Couldn't send message. Try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="page-first-section relative" data-testid="contact-section" style={{ paddingBottom: "clamp(2.5rem,6vw,6rem)" }}>
      <Watermark word="CONNECT" />
      <div className="mt-container relative z-10">
        <div className="text-center max-w-3xl mx-auto mt-enter">
          <p className="font-mono text-[0.7rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)" }}>Reach Out</p>
          <h1 className="mt-3 font-display font-extralight leading-[1.08] text-[clamp(2.6rem,7vw,5.5rem)]">
            Let's build something <span className="italic mt-chrome-text mt-keep-anim">together.</span>
          </h1>
          <p className="mt-5 font-display font-light italic" style={{ color: "var(--mt-text-muted)" }}>
            Press, partnerships, or a thread of your own. Leave us a note.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="mx-auto mt-12 max-w-2xl mt-glass mt-rainbow-edge rounded-3xl p-6 sm:p-10 mt-enter"
          data-testid="contact-form"
          style={{ transitionDelay: "120ms" }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Name">
              <input
                name="name"
                type="text"
                value={form.name}
                onChange={onChange}
                className="mt-input"
                placeholder="Your name"
                data-testid="contact-name"
                required
              />
            </Field>
            <Field label="Email">
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                className="mt-input"
                placeholder="you@domain.com"
                data-testid="contact-email"
                required
              />
            </Field>
          </div>
          <Field label="Message" className="mt-5">
            <textarea
              name="message"
              rows={5}
              value={form.message}
              onChange={onChange}
              className="mt-input resize-none"
              placeholder="Tell us what you're building."
              data-testid="contact-message"
              required
            />
          </Field>
          <div className="mt-7 flex items-center justify-between gap-4">
            <p
  className="font-mono text-[0.62rem] tracking-mt uppercase"
  style={{ color: "var(--mt-text-muted)" }}
>
  We respond within 24 hours.
</p>
            <button type="submit" className="mt-btn mt-rainbow-edge" disabled={submitting} data-testid="contact-submit">
              {submitting ? "Sending..." : "Send Message"} <Send size={14} />
            </button>
          </div>
        </form>

        {/* Socials */}
        <div className="mx-auto mt-10 max-w-2xl mt-glass rounded-full px-6 py-4 flex items-center justify-around mt-enter" style={{ transitionDelay: "200ms" }} data-testid="contact-socials">
          <SocialLink href="https://instagram.com/millionthreads.store" label="Instagram" Icon={Instagram} />
          <span className="w-px h-6" style={{ background: "var(--mt-glass-border)" }} />
          <SocialLink href="mailto:support.millionthreads@gmail.com" label="Email" Icon={Mail} />
          <span className="w-px h-6" style={{ background: "var(--mt-glass-border)" }} />
          <SocialLink href="https://wa.me/918282081648" label="WhatsApp" Icon={MessageCircle} />
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span
  className="font-mono text-[0.62rem] tracking-mt uppercase mb-2 block"
  style={{ color: "var(--mt-text)" }}
>
  {label}
</span>
      {children}
    </label>
  );
}

function SocialLink({ href, label, Icon }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="flex items-center gap-3 px-3 py-1.5 rounded-full group transition" data-testid={`social-${label.toLowerCase()}`}>
      <span className="inline-flex items-center justify-center w-9 h-9 rounded-full mt-glass transition group-hover:scale-110" style={{ boxShadow: "0 0 0 0 rgba(0,170,255,0)" }}>
        <Icon
  size={15}
  style={{ color: "var(--mt-text)" }}
/>
      </span>
     <span
  className="font-mono text-[0.65rem] tracking-mt uppercase"
  style={{ color: "var(--mt-text)" }}
>
  {label}
</span>
    </a>
  );
}
